import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { AccessTime as TimeIcon } from '@mui/icons-material';
import { useAuthRepository } from '../../../../../infra/auth';

const SESSION_START_KEY = 'SESSION_START_TIMESTAMP';

export const SessionTimer = () => {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const timeoutSecondsRef = useRef<number | null>(null);
  const { getSessionConfig } = useAuthRepository();

  const handleLogout = useCallback(() => {
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem(SESSION_START_KEY);
    const loginUrl =
      process.env.REACT_APP_VELES_LOGIN_URL ||
      (process.env.REACT_APP_VELES_MODULES_URL || '').replace('/modulos', '/authentication/login');
    window.location.href = loginUrl;
  }, []);

  const calculateTimeLeft = useCallback(() => {
    const token = localStorage.getItem('ACCESS_TOKEN');
    if (!token) return null;

    const timeoutSec = timeoutSecondsRef.current;
    if (timeoutSec === null || timeoutSec <= 0) return null;

    let start = localStorage.getItem(SESSION_START_KEY);
    if (!start) {
      start = Date.now().toString();
      localStorage.setItem(SESSION_START_KEY, start);
    }

    const elapsedSeconds = Math.floor((Date.now() - parseInt(start, 10)) / 1000);
    const remaining = timeoutSec - elapsedSeconds;

    return Math.max(0, remaining);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchConfigAndInit = async () => {
      try {
        const config = await getSessionConfig();
        if (!isMounted) return;

        const timeoutSec = config?.result?.sessionTimeoutSeconds;
        if (timeoutSec) {
          timeoutSecondsRef.current = timeoutSec;
          
          if (!localStorage.getItem(SESSION_START_KEY)) {
            localStorage.setItem(SESSION_START_KEY, Date.now().toString());
          }

          const initial = calculateTimeLeft();
          if (initial !== null) {
            setTimeLeft(initial);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de sessão:', error);
      }
    };

    fetchConfigAndInit();

    const timer = setInterval(() => {
      if (timeoutSecondsRef.current === null) return;

      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining !== null && remaining <= 0) {
        clearInterval(timer);
        handleLogout();
      }
    }, 1000);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [getSessionConfig, calculateTimeLeft, handleLogout]);

  if (timeLeft === null) return null;

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}h ${mins.toString().padStart(2, '0')}m`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isLowTime = timeLeft < 300; // 5 minutos

  return (
    <Tooltip title="Tempo restante de sessão antes do logout automático" arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          icon={<TimeIcon sx={{ fontSize: '1rem !important', color: isLowTime ? '#ff1744' : '#ee9338' }} />}
          label={formatTime(timeLeft)}
          size="small"
          sx={{
            fontWeight: 800,
            fontFamily: 'monospace',
            color: isLowTime ? '#ff1744' : '#ee9338',
            backgroundColor: '#ffffff',
            border: '1.5px solid',
            borderColor: isLowTime ? '#ff1744' : '#ee9338',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            px: 0.5,
            transition: 'all 0.3s ease',
            '& .MuiChip-label': { px: 1 },
            animation: isLowTime ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.7 },
              '100%': { opacity: 1 },
            }
          }}
        />
      </Box>
    </Tooltip>
  );
};
